import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import type { OrgRole, Role, Store, StoreGroup } from '../api/types';
import { AppHeader } from '../components/AppHeader';
import OrgManagePanel from '../components/OrgManagePanel';
import Toast, { type ToastMsg } from '../components/Toast';

function errDetail(err: unknown): string {
  return err instanceof HttpError ? err.detail : 'unknown error';
}

/** Blank (or junk) means "no gate" — the server stores null for an ungated store. */
function limitValue(raw: string): number | null {
  const n = Number(raw.trim());
  return raw.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null;
}

const UPGRADE_CONFIRM =
  'Upgrading re-renders every product in this store once on the next run. Continue?';

/** One store in the selected org. The spend gates are edited as drafts, so each
 *  row owns its own state — hooks can't run inside a .map() callback. */
function StoreRow({
  store, groups, onMove, onUnassign, onSaveLimits, onUpgradeHash,
}: {
  store: Store;
  groups: StoreGroup[];
  onMove: (groupId: string | null) => void;
  onUnassign: () => void;
  onSaveLimits: (maxProducts: number | null, maxRunUsd: number | null) => void;
  onUpgradeHash: () => void;
}) {
  const [maxProducts, setMaxProducts] = useState(store.max_products?.toString() ?? '');
  const [maxRunUsd, setMaxRunUsd] = useState(store.max_run_usd?.toString() ?? '');
  // Absent means the store predates the used-inputs payload; only 4 is current.
  const legacyHash = (store.hash_schema ?? 3) !== 4;

  return (
    // The name column needs a floor: at minmax(0,1fr) the control cluster claims its
    // max-content width and a legacy-hash row (limits + badge + Upgrade + Unassign)
    // crushes the store name to a single letter. With a floor the controls wrap.
    <div
      data-testid={`store-row-${store.id}`}
      className="grid gap-3 py-4 lg:grid-cols-[minmax(12rem,1fr)_auto] lg:items-center"
    >
      <div className="min-w-0">
        <p className="truncate font-semibold text-ff-ink">{store.name}</p>
        <p className="truncate font-data text-[11px] text-ff-mute">{store.id}</p>
        <details className="mt-1 text-[11px] text-ff-mute" data-testid={`image-map-${store.id}`}>
          <summary className="cursor-pointer">Image map (BigQuery)</summary>
          <ImageMapTable storeId={store.id} />
        </details>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid={`platform-store-group-${store.id}`}
          value={store.group_id ?? ''}
          onChange={(e) => onMove(e.target.value || null)}
          aria-label={`Group for ${store.name}`}
          className="ff-input-sm"
        >
          <option value="">Ungrouped</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>

        <input
          data-testid={`limit-max-products-${store.id}`}
          type="number" min={0} value={maxProducts}
          onChange={(e) => setMaxProducts(e.target.value)}
          aria-label={`Max products per run for ${store.name}`}
          placeholder="products" className="ff-input-sm w-28 font-data"
        />
        <input
          data-testid={`limit-max-run-usd-${store.id}`}
          type="number" min={0} step="0.5" value={maxRunUsd}
          onChange={(e) => setMaxRunUsd(e.target.value)}
          aria-label={`Max spend per run in USD for ${store.name}`}
          placeholder="$ per run" className="ff-input-sm w-28 font-data"
        />
        <button
          type="button" data-testid={`save-limits-${store.id}`}
          onClick={() => onSaveLimits(limitValue(maxProducts), limitValue(maxRunUsd))}
          className="ff-btn-ghost px-3 py-1.5 text-xs"
        >
          Save limits
        </button>

        {legacyHash && (
          <>
            <span data-testid={`hash-badge-${store.id}`} className="ff-chip bg-ff-warning-soft text-ff-warning">
              Legacy hashing
            </span>
            <button
              type="button" data-testid={`upgrade-hash-${store.id}`}
              onClick={() => { if (window.confirm(UPGRADE_CONFIRM)) onUpgradeHash(); }}
              className="ff-btn-ghost px-3 py-1.5 text-xs"
            >
              Upgrade to v4
            </button>
          </>
        )}

        <button
          type="button" data-testid={`unassign-store-${store.id}`}
          onClick={onUnassign} className="ff-btn-danger px-3 py-1.5 text-xs"
        >
          Unassign
        </button>
      </div>
    </div>
  );
}

function ImageMapTable({ storeId }: { storeId: string }) {
  // History size in the key: the Feeds page (FeedsTab) reads the same store's
  // status with history=10 — without it here the two differently-shaped
  // responses would share one cache entry (review Finding 1).
  const q = useQuery({ queryKey: ['feed-status', storeId, 0], queryFn: () => api.feedStatus(storeId, 0) });
  return <code className="font-data">{q.data?.image_map_table ?? '…'}</code>;
}

export default function Platform() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [newOrg, setNewOrg] = useState({ id: '', name: '' });
  const [assignGroup, setAssignGroup] = useState<Record<string, string>>({});

  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: api.listOrgs });
  const storesQ = useQuery({ queryKey: ['stores'], queryFn: api.listStores });
  const orgQ = useQuery({
    queryKey: ['org', selected], queryFn: () => api.getOrg(selected!), enabled: !!selected,
  });
  const usersQ = useQuery({ queryKey: ['admin-users'], queryFn: api.adminListUsers });

  const orgs = orgsQ.data ?? [];
  const selectedOrg = orgs.find((o) => o.id === selected);
  const groups = orgQ.data?.org.groups ?? [];
  const users = usersQ.data ?? [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['orgs'] });
    if (selected) qc.invalidateQueries({ queryKey: ['org', selected] });
    qc.invalidateQueries({ queryKey: ['stores'] });
    qc.invalidateQueries({ queryKey: ['admin-users'] });
  };
  const onErr = (err: unknown) => setToast({ kind: 'error', msg: errDetail(err) });

  function pickOrg(org: { id: string; name: string }) {
    setSelected(org.id);
    setOrgName(org.name);
    // Group ids are org-scoped: a pick carried over from another org is a
    // foreign id the server would reject.
    setAssignGroup({});
  }

  // --- orgs ---
  const createOrg = useMutation({
    mutationFn: () => api.createOrg({ id: newOrg.id.trim(), name: newOrg.name.trim() }),
    onSuccess: (org) => { setNewOrg({ id: '', name: '' }); pickOrg(org); refresh(); },
    onError: onErr,
  });
  const renameOrg = useMutation({
    mutationFn: () => api.renameOrg(selected!, { name: orgName.trim() }),
    onSuccess: refresh,
    onError: onErr,
  });
  // The server refuses (409) while stores or users remain — that lands in the toast.
  const deleteOrg = useMutation({
    mutationFn: (orgId: string) => api.deleteOrg(orgId),
    onSuccess: () => { setSelected(null); refresh(); },
    onError: onErr,
  });

  // --- stores ---
  const moveStore = useMutation({
    mutationFn: (v: { storeId: string; groupId: string | null }) =>
      api.assignStoreToOrg(selected!, v.storeId, { group_id: v.groupId }),
    onSuccess: refresh,
    onError: onErr,
  });
  const unassignStore = useMutation({
    mutationFn: (storeId: string) => api.unassignStore(selected!, storeId),
    onSuccess: refresh,
    onError: onErr,
  });
  // PUT replaces the WHOLE store record, so every write spreads the row it patches.
  const saveStore = useMutation({
    mutationFn: (v: { store: Store; patch: Partial<Store> }) =>
      api.updateStore(v.store.id, { ...v.store, ...v.patch }),
    onSuccess: refresh,
    onError: onErr,
  });

  // The org detail carries its own copy of the same stores; writes go through the
  // /api/stores row so one cache is the source of truth for what we PUT back.
  const byId = useMemo(
    () => new Map((storesQ.data ?? []).map((s) => [s.id, s] as const)),
    [storesQ.data],
  );
  const orgStores = useMemo(
    () => (orgQ.data?.stores ?? []).map((s) => byId.get(s.id) ?? s),
    [orgQ.data, byId],
  );
  const unassigned = useMemo(
    () => (storesQ.data ?? []).filter((s) => !s.org_id),
    [storesQ.data],
  );

  // --- all accounts ---
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('client');
  const [inviteOrg, setInviteOrg] = useState('');
  const [inviteGroups, setInviteGroups] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const inviteOrgGroups = orgs.find((o) => o.id === inviteOrg)?.groups ?? [];

  const invite = useMutation({
    mutationFn: () =>
      api.adminCreateUser({
        email: email.trim(),
        role,
        // A super admin belongs to no tenant and a client must have one —
        // POST /admin/users 400s either way round.
        org_id: role === 'admin' ? null : inviteOrg || null,
        // An invite from here is the tenant's first account: its owner. Per-person
        // roles are managed inside the org's own People tab.
        org_role: 'owner',
        group_ids: role === 'admin' ? [] : inviteGroups,
      }),
    onSuccess: (res) => {
      setEmail('');
      setInviteGroups([]);
      setToken(res.set_password_token);
      refresh();
    },
    onError: onErr,
  });
  const setUserOrg = useMutation({
    mutationFn: (v: { uid: string; orgId: string | null; orgRole: OrgRole }) =>
      api.adminSetUserOrg(v.uid, { org_id: v.orgId, org_role: v.orgRole, group_ids: [] }),
    onSuccess: refresh,
    onError: onErr,
  });
  const toggleActive = useMutation({
    mutationFn: (v: { uid: string; active: boolean }) => api.adminSetUserActive(v.uid, v.active),
    onSuccess: refresh,
    onError: onErr,
  });

  // Token rides in the URL FRAGMENT (#token=...), not the query string — the
  // fragment never leaves the browser (no Referer / server / proxy logs; H5).
  const setPasswordUrl = token
    ? `${window.location.origin}/set-password#token=${encodeURIComponent(token)}`
    : null;

  return (
    <div className="min-h-screen bg-ff-ivory relative" data-testid="platform-page">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" aria-hidden="true" />

      <AppHeader active="platform" />

      <main className="relative mx-auto max-w-[1240px] px-5 pb-24 pt-10 animate-rise sm:px-8 sm:pt-14">
        <div className="border-b border-ff-line pb-8">
          <p className="ff-kicker mb-3">Super admin</p>
          <h1 className="ff-page-title text-[clamp(3rem,7vw,4.5rem)] leading-[0.95]">
            Platform<span className="text-ff-orange">.</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-ff-mute">
            Organizations, store assignment, spend gates and every account on the platform.
          </p>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          {/* Left rail — organizations */}
          <aside className="space-y-4">
            <div className="ff-card p-4">
              <h2 className="mb-3 font-display text-lg font-bold text-ff-ink">Organizations</h2>
              {orgsQ.isLoading && <p className="text-sm text-ff-mute">Loading…</p>}
              {orgsQ.isSuccess && orgs.length === 0 && (
                <p className="text-sm text-ff-mute">No organizations yet.</p>
              )}
              <div className="flex flex-col gap-1">
                {orgs.map((o) => (
                  <button
                    key={o.id} type="button" data-testid={`org-rail-item-${o.id}`}
                    onClick={() => pickOrg(o)}
                    aria-pressed={selected === o.id}
                    className={`rounded-xl px-3 py-2 text-left transition-colors ${
                      selected === o.id ? 'bg-ff-ink text-white' : 'hover:bg-ff-paper'
                    }`}
                  >
                    <span className="block truncate text-sm font-semibold">{o.name}</span>
                    <span className={`block text-[11px] ${selected === o.id ? 'text-white/70' : 'text-ff-mute'}`}>
                      {o.n_stores} stores · {o.n_users} people
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="ff-card p-4">
              <h2 className="mb-3 font-display text-lg font-bold text-ff-ink">New organization</h2>
              <label htmlFor="new-org-id" className="ff-label">Id</label>
              <input
                id="new-org-id" data-testid="new-org-id" value={newOrg.id}
                onChange={(e) => setNewOrg({ ...newOrg, id: e.target.value })}
                placeholder="acme" className="ff-input font-data"
              />
              <label htmlFor="new-org-name" className="ff-label mt-3">Name</label>
              <input
                id="new-org-name" data-testid="new-org-name" value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                placeholder="Acme" className="ff-input"
              />
              <button
                type="button" data-testid="create-org" onClick={() => createOrg.mutate()}
                disabled={createOrg.isPending || !newOrg.id.trim() || !newOrg.name.trim()}
                className="ff-btn-primary mt-4 w-full"
              >
                {createOrg.isPending ? 'Creating…' : 'Create organization'}
              </button>
            </div>
          </aside>

          {/* Right pane — the selected org */}
          <section className="min-w-0 space-y-8">
            {!selected && (
              <div className="rounded-2xl border border-dashed border-ff-line bg-white/70 px-6 py-14 text-center">
                <p className="font-display text-xl font-bold text-ff-ink">Pick an organization.</p>
                <p className="mt-2 text-sm text-ff-mute">
                  Its stores, groups and people open here.
                </p>
              </div>
            )}

            {selected && (
              <>
                <div className="ff-card flex flex-wrap items-end gap-3 p-5">
                  <div className="min-w-0 flex-1">
                    <label htmlFor="org-name" className="ff-label">Organization name</label>
                    <input
                      id="org-name" value={orgName} onChange={(e) => setOrgName(e.target.value)}
                      onBlur={() => { if (orgName.trim() && orgName.trim() !== selectedOrg?.name) renameOrg.mutate(); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      className="ff-input"
                    />
                    <p className="mt-1.5 font-data text-[11px] text-ff-mute">{selected}</p>
                  </div>
                  <button
                    type="button" data-testid="enter-org" onClick={() => nav(`/stores?org=${selected}`)}
                    className="ff-btn-primary px-5"
                  >
                    Enter org
                  </button>
                  <button
                    type="button" data-testid="delete-org"
                    onClick={() => {
                      if (window.confirm(`Delete "${selectedOrg?.name ?? selected}"? This only works once no stores or people remain in it.`)) {
                        deleteOrg.mutate(selected);
                      }
                    }}
                    className="ff-btn-danger px-5"
                  >
                    Delete
                  </button>
                </div>

                <div className="ff-card p-5 sm:p-7">
                  <h2 className="mb-1 font-display text-2xl font-bold text-ff-ink">Stores in this org</h2>
                  <p className="mb-4 text-xs text-ff-mute">
                    Limits gate what a run may spend: products per run and dollars per run. Blank means ungated.
                  </p>
                  {orgQ.isLoading && <p className="text-sm text-ff-mute">Loading…</p>}
                  {orgQ.isSuccess && orgStores.length === 0 && (
                    <p className="text-sm text-ff-mute">No stores in this organization yet.</p>
                  )}
                  <div className="divide-y divide-ff-sand/60">
                    {orgStores.map((s) => (
                      <StoreRow
                        key={s.id} store={s} groups={groups}
                        onMove={(groupId) => moveStore.mutate({ storeId: s.id, groupId })}
                        onUnassign={() => unassignStore.mutate(s.id)}
                        onSaveLimits={(maxProducts, maxRunUsd) =>
                          saveStore.mutate({ store: s, patch: { max_products: maxProducts, max_run_usd: maxRunUsd } })}
                        onUpgradeHash={() => saveStore.mutate({ store: s, patch: { hash_schema: 4 } })}
                      />
                    ))}
                  </div>
                </div>

                <div className="ff-card p-5 sm:p-7">
                  <h2 className="mb-4 font-display text-2xl font-bold text-ff-ink">Unassigned stores</h2>
                  {unassigned.length === 0 ? (
                    <p className="text-sm text-ff-mute">Every store belongs to an organization.</p>
                  ) : (
                    <div className="divide-y divide-ff-sand/60">
                      {unassigned.map((s) => (
                        <div
                          key={s.id} data-testid={`unassigned-store-${s.id}`}
                          className="flex flex-wrap items-center justify-between gap-3 py-3"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-ff-ink">{s.name}</p>
                            <p className="truncate font-data text-[11px] text-ff-mute">{s.id}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              data-testid={`assign-store-group-${s.id}`} value={assignGroup[s.id] ?? ''}
                              onChange={(e) => setAssignGroup({ ...assignGroup, [s.id]: e.target.value })}
                              aria-label={`Group for ${s.name}`} className="ff-input-sm"
                            >
                              <option value="">Ungrouped</option>
                              {groups.map((g) => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                              ))}
                            </select>
                            <button
                              type="button" data-testid={`assign-store-${s.id}`}
                              onClick={() => moveStore.mutate({ storeId: s.id, groupId: assignGroup[s.id] || null })}
                              className="ff-btn-ghost px-4 py-1.5 text-xs"
                            >
                              Assign
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="mb-4 font-display text-2xl font-bold text-ff-ink">Groups &amp; people</h2>
                  <OrgManagePanel orgId={selected} />
                </div>
              </>
            )}
          </section>
        </div>

        {/* All accounts — platform-wide, independent of the selected org */}
        <section className="mt-16 border-t border-ff-line pt-10">
          <h2 className="mb-5 font-display text-2xl font-bold text-ff-ink">All accounts</h2>

          <div className="ff-card p-5 sm:p-7">
            <h3 className="mb-4 font-display text-lg font-bold text-ff-ink">Invite an account</h3>
            <div className="grid items-end gap-3 sm:grid-cols-[2fr_1fr_1.5fr_auto]">
              <div>
                <label htmlFor="admin-invite-email" className="ff-label">Email</label>
                <input
                  id="admin-invite-email" data-testid="admin-invite-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="person@company.com" className="ff-input"
                />
              </div>
              <div>
                <label htmlFor="admin-invite-role" className="ff-label">Role</label>
                <select
                  id="admin-invite-role" data-testid="admin-invite-role" value={role}
                  onChange={(e) => {
                    const next = e.target.value as Role;
                    setRole(next);
                    if (next === 'admin') { setInviteOrg(''); setInviteGroups([]); }
                  }}
                  className="ff-input"
                >
                  <option value="client">client</option>
                  <option value="admin">admin</option>
                </select>
              </div>
              <div>
                <label htmlFor="admin-invite-org" className="ff-label">Organization</label>
                <select
                  id="admin-invite-org" data-testid="admin-invite-org" value={inviteOrg}
                  disabled={role === 'admin'}
                  onChange={(e) => { setInviteOrg(e.target.value); setInviteGroups([]); }}
                  className="ff-input disabled:opacity-45"
                >
                  <option value="">{role === 'admin' ? 'none — platform wide' : 'Pick an organization'}</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <button
                type="button" data-testid="admin-invite-submit" onClick={() => invite.mutate()}
                disabled={invite.isPending || !email.trim() || (role === 'client' && !inviteOrg)}
                className="ff-btn-primary py-3"
              >
                {invite.isPending ? 'Inviting…' : 'Invite'}
              </button>
            </div>

            {role === 'client' && inviteOrgGroups.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3">
                {inviteOrgGroups.map((g) => (
                  <label key={g.id} className="ff-chip cursor-pointer border border-ff-line bg-white normal-case">
                    <input
                      type="checkbox" data-testid={`admin-invite-group-${g.id}`}
                      checked={inviteGroups.includes(g.id)}
                      onChange={() => setInviteGroups(
                        inviteGroups.includes(g.id)
                          ? inviteGroups.filter((x) => x !== g.id)
                          : [...inviteGroups, g.id],
                      )}
                      className="mr-1.5"
                    />
                    {g.name}
                  </label>
                ))}
              </div>
            )}

            {setPasswordUrl && (
              <div className="mt-5 rounded-xl border border-ff-forest/20 bg-ff-mint/30 p-4">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-widish text-ff-ink-soft">
                  One-time set-password link — copy &amp; send
                </p>
                <div className="flex gap-2">
                  <input
                    data-testid="set-password-link" readOnly value={setPasswordUrl}
                    className="ff-input font-data text-xs flex-1"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button" className="ff-btn-ghost px-4"
                    onClick={() => {
                      navigator.clipboard?.writeText(setPasswordUrl).then(
                        () => setToast({ kind: 'success', msg: 'Link copied.' }),
                        () => setToast({ kind: 'error', msg: 'Copy failed — select and copy manually.' }),
                      );
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6">
            {usersQ.isLoading ? (
              <p className="text-sm text-ff-mute">Loading…</p>
            ) : users.length === 0 ? (
              <p className="text-sm text-ff-mute">No accounts yet.</p>
            ) : (
              <div className="divide-y divide-ff-sand/60 border-t border-b border-ff-sand/60">
                {users.map((u) => (
                  <div
                    key={u.id} data-testid={`admin-user-row-${u.id}`}
                    className="flex flex-wrap items-center justify-between gap-3 py-4"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-ff-ink/90">{u.email}</p>
                      <p className="text-[11px] font-semibold uppercase tracking-widish text-ff-mute">
                        {u.role}{u.disabled ? ' · disabled' : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <select
                        data-testid={`admin-user-org-${u.id}`} value={u.org_id ?? ''}
                        disabled={u.role === 'admin'}
                        title={u.role === 'admin' ? 'Super admins belong to no organization' : undefined}
                        aria-label={`Organization for ${u.email}`}
                        onChange={(e) => setUserOrg.mutate({
                          uid: u.id,
                          orgId: e.target.value || null,
                          orgRole: u.org_role ?? 'member',
                        })}
                        className="ff-input-sm disabled:opacity-45"
                      >
                        <option value="">No organization</option>
                        {orgs.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                      <button
                        type="button" data-testid={`admin-toggle-active-${u.id}`}
                        onClick={() => toggleActive.mutate({ uid: u.id, active: u.disabled })}
                        className="ff-btn-ghost px-4 py-2 text-xs"
                      >
                        {u.disabled ? 'Reactivate' : 'Deactivate'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
